import { useRef, useState } from "react";
import { FaTimes, FaWhatsapp, FaDownload, FaSpinner, FaReceipt } from "react-icons/fa";
import { formatMonto } from "../../utils/rentalCalculations";
import { useWhatsappImageShare } from "../../hooks/useWhatsappImageShare";
import logo from "../../assets/img/logos/logoColor.png";
import ModalPortal from "../ModalPortal";

const slugify = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Modal con el comprobante de pago al propietario, descargable/compartible.
export default function ComprobantePagoModal({ group, periodo, onClose }) {
  const cardRef = useRef(null);

  const liq = group?.liquidacion;
  // Historial de pagos (tandas). Cada tanda tiene su propio comprobante para
  // no reenviar los alquileres ya notificados en pagos anteriores.
  const pagosHist = Array.isArray(liq?.pagos) ? liq.pagos : [];
  // Por defecto se muestra el último pago (el que se acaba de hacer).
  // "all" = comprobante acumulado (liquidaciones viejas sin historial).
  const [pagoIdx, setPagoIdx] = useState(pagosHist.length > 0 ? pagosHist.length - 1 : "all");
  const selPago = pagoIdx === "all" ? null : pagosHist[pagoIdx];

  // Contratos incluidos en el comprobante: los de la tanda elegida, o todos los
  // pagados si es el acumulado.
  const contratosPagados = !group
    ? []
    : selPago
    ? group.contratos.filter((c) => (selPago.contratoIds || []).includes(c.idFirebase))
    : group.contratos.filter((c) => c.pagadoAlDueno);

  // Desglose de los contratos liquidados: total del alquiler y comisión, para
  // que el propietario vea cómo se llega al total pagado (Total - Comisión).
  const totalCobrado = contratosPagados.reduce((s, c) => s + (Number(c.montoActual) || 0), 0);
  const totalComision = contratosPagados.reduce((s, c) => s + (Number(c.comisionMonto) || 0), 0);
  const totalAPagar = contratosPagados.reduce((s, c) => s + (Number(c.aPagar) || 0), 0);

  const montoPagado = selPago ? Number(selPago.monto) || 0 : group?.montoPagado || 0;
  // Los gastos se descuentan en el primer pago: la diferencia entre lo "a pagar"
  // y lo efectivamente pagado en esa tanda son los gastos descontados.
  const gastosDescontados = selPago
    ? Math.max(0, totalAPagar - montoPagado)
    : group?.totalGastos || 0;

  const fecha = selPago ? selPago.fecha : liq?.fechaPago || "";
  const fechaFmt = fecha
    ? new Date(fecha + "T00:00:00").toLocaleDateString("es-AR")
    : "";
  const observaciones = selPago ? selPago.observaciones || "" : liq?.observaciones || "";
  const fileName = `Comprobante-${slugify(group?.locadorNombre)}-${slugify(periodo)}${
    selPago ? `-pago-${pagoIdx + 1}` : ""
  }.png`;

  const mensaje = [
    `Comprobante de pago - ${group?.locadorNombre}`,
    `${periodo}`,
    `Total pagado: ${formatMonto(montoPagado)}`,
    `Inmobiliaria Plorutti`,
  ].join("\n");

  const { generating, feedback, waReady, abrirWhatsapp, descargar, compartir } = useWhatsappImageShare({
    cardRef,
    fileName,
    telefono: group?.telefono,
    caption: mensaje,
  });

  if (!group) return null;

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 py-6 overflow-y-auto">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full my-auto ${pagosHist.length > 1 ? "max-w-4xl" : "max-w-md"}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 inline-flex items-center gap-2">
            <FaReceipt className="text-primary" /> Comprobante de pago
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        <div className={`p-4 max-h-[75vh] overflow-y-auto bg-gray-50 dark:bg-gray-900/30 ${pagosHist.length > 1 ? "grid grid-cols-1 md:grid-cols-2 gap-5" : ""}`}>
          {pagosHist.length > 1 && (
            <div className="content-start">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                ¿Qué pago querés enviar?
              </label>
              <div className="space-y-2">
                {pagosHist.map((p, i) => {
                  const activo = pagoIdx === i;
                  const props = (p.contratoIds || [])
                    .map((id) => group.contratos.find((c) => c.idFirebase === id))
                    .filter(Boolean);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPagoIdx(i)}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${
                        activo
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                          Pago {i + 1}
                          {p.fecha && (
                            <span className="ml-2 text-[11px] font-normal text-gray-400">
                              {new Date(p.fecha + "T00:00:00").toLocaleDateString("es-AR")}
                            </span>
                          )}
                        </span>
                        <span className="text-sm font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                          {formatMonto(Number(p.monto) || 0)}
                        </span>
                      </div>
                      {props.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {props.map((c) => (
                            <li key={c.idFirebase} className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              {c.propiedad} · {c.locatarioNombre || "—"}
                            </li>
                          ))}
                        </ul>
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setPagoIdx("all")}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    pagoIdx === "all"
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Acumulado</span>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                      {formatMonto(group.montoPagado)}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">Todos los pagos juntos</p>
                </button>
              </div>
            </div>
          )}

          {/* Comprobante (lo que se captura como imagen) */}
          <div ref={cardRef} className="bg-white text-gray-800 w-full max-w-sm mx-auto p-6 rounded-lg self-start">
            <div className="flex items-center gap-3 pb-3 border-b-2 border-gray-200">
              <img src={logo} alt="Hugo A. Plorutti y Cía" className="h-12 w-auto object-contain" />
              <div className="leading-tight">
                <p className="text-[13px] font-semibold text-gray-700">Comprobante de pago al propietario</p>
              </div>
            </div>

            <div className="py-3 space-y-1 text-[13px] border-b border-gray-100">
              <div className="flex justify-between"><span className="text-gray-500">Propietario</span><span className="font-semibold">{group.locadorNombre}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Período</span><span className="font-semibold">{periodo}</span></div>
              {fechaFmt && <div className="flex justify-between"><span className="text-gray-500">Fecha de pago</span><span className="font-semibold">{fechaFmt}</span></div>}
            </div>

            {contratosPagados.length > 0 && (
              <div className="py-3 border-b border-gray-100">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Detalle</p>
                <ul className="space-y-1">
                  {contratosPagados.map((c) => (
                    <li key={c.idFirebase} className="flex justify-between text-[12px]">
                      <span className="text-gray-600 truncate pr-2">{c.locatarioNombre || "—"} · {c.propiedad}</span>
                      <span className="font-medium whitespace-nowrap">{formatMonto(c.aPagar, c.moneda)}</span>
                    </li>
                  ))}
                </ul>
                {gastosDescontados > 0 && (
                  <div className="flex justify-between text-[12px] text-red-500 mt-1">
                    <span>Gastos descontados</span>
                    <span className="font-medium">-{formatMonto(gastosDescontados)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="pt-3 space-y-1">
              {contratosPagados.length > 0 && (
                <>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Total</span>
                    <span className="font-semibold">{formatMonto(totalCobrado)}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Administración</span>
                    <span className="font-semibold text-red-500">-{formatMonto(totalComision)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center pt-1 mt-1 border-t border-gray-100">
                <span className="text-[13px] font-bold text-gray-700">TOTAL PAGADO</span>
                <span className="text-xl font-extrabold text-green-600">{formatMonto(montoPagado)}</span>
              </div>
              {observaciones && (
                <p className="text-[11px] text-gray-500 pt-1">Forma de pago: {observaciones}</p>
              )}
            </div>

            <div className="mt-6 pt-8 flex justify-end">
              <div className="text-center">
                <div className="border-t border-gray-400 w-40" />
                <p className="text-[10px] text-gray-500 mt-1">Firma y aclaración</p>
              </div>
            </div>
          </div>
        </div>

        {feedback && (
          <div
            className={`mx-4 mb-2 mt-3 rounded-lg px-3 py-2 text-xs ${
              feedback.type === "success"
                ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                : feedback.type === "info"
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            }`}
          >
            {feedback.msg}
          </div>
        )}

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Cerrar
          </button>
          <button
            onClick={descargar}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
          >
            {generating ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaDownload className="w-3.5 h-3.5" />}
            Descargar
          </button>
          {waReady ? (
            <button
              onClick={abrirWhatsapp}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
              title="Abre la app de WhatsApp o, si no está, WhatsApp Web"
            >
              <FaWhatsapp className="w-4 h-4" /> Abrir WhatsApp
            </button>
          ) : (
            <button
              onClick={compartir}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50"
              title="Compartir el comprobante por WhatsApp (adjunta o copia la imagen)"
            >
              {generating ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaWhatsapp className="w-4 h-4" />}
              WhatsApp
            </button>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
